import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { Trash2, CalendarClock, Timer, Briefcase, Image as ImageIcon } from "lucide-react";
import PhotoLightbox from "@/components/PhotoLightbox";

/** Feed of end-of-day summaries operatives have sent. Manager view. */
export default function DaySummariesPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null); // { photos: string[], index: number }

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/day-summaries");
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const remove = async (id) => {
        if (!confirm("Delete this summary?")) return;
        try {
            await api.delete(`/day-summaries/${id}`);
            setItems(prev => prev.filter(x => x.id !== id));
            toast.success("Deleted");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading) return <div className="text-sm text-[var(--text-muted)]">Loading…</div>;

    if (items.length === 0) {
        return (
            <div className="card-flat p-8 text-center" data-testid="summaries-empty">
                <div className="font-display text-lg font-semibold">No summaries yet</div>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                    When your crew wrap their day and tap "Send today's summary," their recap lands here.
                </p>
            </div>
        );
    }

    return (
        <section className="space-y-3" data-testid="summaries-feed">
            {items.map(s => (
                <article key={s.id} className="card-flat p-4">
                    <div className="flex items-center gap-3">
                        <div
                            className="h-10 w-10 rounded-full flex items-center justify-center font-semibold text-sm border-2 shrink-0"
                            style={{
                                background: s.user_color || "var(--pine-light)",
                                color: s.user_color ? "#fff" : "var(--pine)",
                                borderColor: s.user_color || "transparent",
                            }}
                        >
                            {initials(s.user_name)}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="font-semibold">{s.user_name}</div>
                            <div className="text-xs text-[var(--text-muted)] flex items-center gap-3 mt-0.5 flex-wrap">
                                <span className="inline-flex items-center gap-1"><CalendarClock size={11} /> {fmtDate(s.date)}</span>
                                <span className="inline-flex items-center gap-1"><Briefcase size={11} /> {s.job_count} job{s.job_count === 1 ? "" : "s"}</span>
                                {s.total_minutes > 0 && (
                                    <span className="inline-flex items-center gap-1"><Timer size={11} /> {fmtDuration(s.total_minutes)}</span>
                                )}
                            </div>
                        </div>
                        <button
                            data-testid={`delete-summary-${s.id}`}
                            onClick={() => remove(s.id)}
                            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-red-600 hover:bg-red-50 transition-colors"
                            aria-label="Delete summary">
                            <Trash2 size={14} />
                        </button>
                    </div>
                    {s.note && (
                        <p className="mt-3 text-sm text-[var(--text-primary)] whitespace-pre-wrap">{s.note}</p>
                    )}
                    {s.photos && s.photos.length > 0 && (
                        <div className="mt-3">
                            <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-semibold mb-1.5 flex items-center gap-1">
                                <ImageIcon size={11} /> {s.photos.length} photo{s.photos.length === 1 ? "" : "s"}
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                {s.photos.map((p, i) => {
                                    const src = photoSrc(p);
                                    return (
                                        <button
                                            key={i}
                                            type="button"
                                            data-testid={`summary-photo-${s.id}-${i}`}
                                            onClick={() => setLightbox({
                                                photos: s.photos.map(photoSrc),
                                                index: i,
                                            })}
                                            className="focus:outline-none focus:ring-2 focus:ring-[var(--pine)] rounded-lg overflow-hidden">
                                            <img src={src} alt="" className="w-full h-24 object-cover hover:opacity-90 transition-opacity" />
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </article>
            ))}

            {lightbox && (
                <PhotoLightbox
                    photos={lightbox.photos}
                    startIndex={lightbox.index}
                    onClose={() => setLightbox(null)}
                />
            )}
        </section>
    );
}

function photoSrc(url) {
    if (url.startsWith("data:")) return url;
    const [summaryId, index] = url.split("#");
    return `${process.env.REACT_APP_BACKEND_URL}/api/day-summaries/${summaryId}/photos/${index}`;
}

function initials(name) {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fmtDate(iso) {
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    } catch { return iso; }
}

function fmtDuration(mins) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
}
